import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../../../../utils/api";

const EditEmployee = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [roles, setRoles] = useState([]);
  const [formData, setFormData] = useState({
    name: "",
    role: "",
    email: "",
    password: "",
    phone: "",
    address: "",
    age: "",
    salary: "",
  });
  const [photo, setPhoto] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [currentPhoto, setCurrentPhoto] = useState(null);
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // fetch roles
  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const response = await api.get("/api/users/roles");
        setRoles(response.data.data);
      } catch (err) {
        console.error("Failed to fetch roles:", err);
      }
    };
    fetchRoles();
  }, []);

  // fetch employee detail
  useEffect(() => {
    const fetchEmployeeData = async () => {
      try {
        const response = await api.get(`/api/employees/${id}`);
        const employee = response.data.data;

        setFormData({
          name: employee.name || employee.user?.name || "",
          role: employee.user?.role || "",
          email: employee.email || employee.user?.email || "",
          password: "",
          phone: employee.phone || "",
          address: employee.address || "",
          age: employee.age ? employee.age.toString() : "",
          salary: employee.salary ? employee.salary.toString() : "",
        });

        if (employee.photo) {
          setCurrentPhoto(employee.photo);
          setPreviewImage(`${employee.photo}`);
        }

        setIsLoading(false);
      } catch (error) {
        console.error("Error fetching employee data:", error);
        setError("Failed to load employee data. Please try again.");
        setIsLoading(false);
      }
    };

    fetchEmployeeData();
  }, [id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const allowedTypes = [
        "image/jpeg",
        "image/jpg",
        "image/png",
        "image/gif",
      ];
      const fileSize = file.size / 1024 / 1024;

      if (!allowedTypes.includes(file.type)) {
        setError("Only image files (JPG, PNG, GIF) are allowed!");
        return;
      }
      if (fileSize > 5) {
        setError("File size must be less than 5MB");
        return;
      }

      setPhoto(file);
      setPreviewImage(URL.createObjectURL(file));
      setError("");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError("");

    const formDataToSend = new FormData();
    Object.keys(formData).forEach((key) => {
      if (formData[key]) {
        formDataToSend.append(key, formData[key]);
      }
    });

    if (photo) formDataToSend.append("photo", photo);

    // kalau role employee & password kosong → hapus password
    if (formData.role === "employee" && !formData.password) {
      formDataToSend.delete("password");
    }

    try {
      await api.put(`/api/employees/${id}`, formDataToSend, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      navigate("/admin/employees");
    } catch (error) {
      console.error("Error updating employee:", error);
      setError(error.response?.data?.message || "Failed to update employee");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="p-6 bg-gray-100 min-h-screen flex items-center justify-center">
        <div className="text-xl">Loading employee data...</div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-100 min-h-screen">
      <div className="max-w-2xl mx-auto">
        <h2 className="text-2xl font-bold mb-4">Edit Employee</h2>
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}
        <form
          onSubmit={handleSubmit}
          className="bg-white shadow-md rounded px-8 pt-6 pb-8 mb-4"
          encType="multipart/form-data"
        >
          {/* Name */}
          <div className="mb-4">
            <label className="block text-gray-700">Name</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className="border rounded w-full py-2 px-3"
              required
            />
          </div>

          {/* Role */}
          <div className="mb-4">
            <label className="block text-gray-700">Role</label>
            <select
              name="role"
              value={formData.role}
              onChange={handleChange}
              className="border rounded w-full py-2 px-3"
              required
            >
              <option value="">-- Select Role --</option>
              {roles.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label} - {r.description}
                </option>
              ))}
            </select>
          </div>

          {/* Email */}
          <div className="mb-4">
            <label className="block text-gray-700">Email</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className="border rounded w-full py-2 px-3"
              required
            />
          </div>

          {/* Password */}
          <div className="mb-4">
            <label className="block text-gray-700">
              Password{" "}
              {formData.role !== "employee" && (
                <span className="text-red-500">*</span>
              )}
            </label>
            <input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              className="border rounded w-full py-2 px-3"
              required={formData.role !== "employee"}
              placeholder="Leave blank to keep current password"
            />
          </div>

          {/* Phone */}
          <div className="mb-4">
            <label className="block text-gray-700">Phone</label>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              className="border rounded w-full py-2 px-3"
            />
          </div>

          {/* Address */}
          <div className="mb-4">
            <label className="block text-gray-700">Address</label>
            <textarea
              name="address"
              value={formData.address}
              onChange={handleChange}
              className="border rounded w-full py-2 px-3"
            />
          </div>

          {/* Age */}
          <div className="mb-4">
            <label className="block text-gray-700">Age</label>
            <input
              type="number"
              name="age"
              value={formData.age}
              onChange={handleChange}
              className="border rounded w-full py-2 px-3"
            />
          </div>

          {/* Salary */}
          <div className="mb-4">
            <label className="block text-gray-700">Salary</label>
            <input
              type="number"
              name="salary"
              value={formData.salary}
              onChange={handleChange}
              className="border rounded w-full py-2 px-3"
            />
          </div>

          {/* Photo */}
          <div className="mb-4">
            <label className="block text-gray-700">Photo</label>
            <input
              type="file"
              name="photo"
              onChange={handleImageChange}
              className="border rounded w-full py-2 px-3"
              accept="image/jpeg,image/png,image/gif"
            />
            <p className="text-xs text-gray-500 mt-1">
              Leave empty to keep current photo
            </p>
            {previewImage && (
              <img
                src={previewImage}
                alt="Preview"
                className="h-40 w-auto object-cover border rounded mt-2"
              />
            )}
          </div>

          {/* Buttons */}
          <div className="flex items-center justify-between">
            <button
              type="submit"
              className={`bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded ${
                isSubmitting ? "opacity-50 cursor-not-allowed" : ""
              }`}
              disabled={isSubmitting}
            >
              {isSubmitting ? "Updating Employee..." : "Update Employee"}
            </button>
            <button
              type="button"
              onClick={() => navigate("/admin/employees")}
              className="bg-gray-500 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded"
              disabled={isSubmitting}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditEmployee;
